import { forwardRef } from 'react';
import { Link, LinkProps, useLocation, useNavigate } from 'react-router-dom';

import { getNavigationInterceptor } from 'src/hooks/use-navigation-interceptor';

// ----------------------------------------------------------------------

interface RouterLinkProps extends Omit<LinkProps, 'to'> {
  href: string;
}

const RouterLink = forwardRef<HTMLAnchorElement, RouterLinkProps>(({ href, onClick, ...other }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Check if we're navigating to a different route
    const interceptor = getNavigationInterceptor();
    if (interceptor && href !== location.pathname) {
      console.log('RouterLink: Navigation intercepted, checking for unsaved changes');
      event.preventDefault(); // Prevent navigation initially

      try {
        const canNavigate = await interceptor();
        if (canNavigate) {
          console.log('RouterLink: Navigation allowed');
          // Call original onClick if provided
          if (onClick) {
            onClick(event);
          }
          // Programmatically navigate since we prevented the default
          navigate(href);
        } else {
          console.log('RouterLink: Navigation blocked due to unsaved changes');
        }
      } catch (error) {
        console.error('RouterLink: Error in navigation interceptor:', error);
      }
      return;
    }

    // Call original onClick if provided
    if (onClick) {
      onClick(event);
    }
  };

  return <Link ref={ref} to={href} onClick={handleClick} {...other} />;
});

export default RouterLink;
